/**
 * Annotation Export Page
 * Extract messages from dora_sessions and create annotation batches
 */

import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Database, BarChart3, FolderOpen } from 'lucide-react';
import { ExportPreview, useExportPreview, useExecuteExport } from '@/features/annotations';
import type { ExportFilters, ExportPreviewResponse } from '@/types/annotation';
import { useI18n } from '@/i18n';

// Sub-navigation tabs - labels resolved at render time
const TAB_PATHS = [
  { path: '/annotation/batches', key: 'batchManagement', icon: FolderOpen },
  { path: '/annotation/export', key: 'dataExport', icon: Database },
  { path: '/annotation/stats', key: 'statsReport', icon: BarChart3 },
];

export function AnnotationExportPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [previewData, setPreviewData] = useState<ExportPreviewResponse | null>(null);

  const previewMutation = useExportPreview();
  const executeMutation = useExecuteExport();

  const handlePreview = async (filters: ExportFilters, sampleSize: number, offset: number = 0, excludeExported: boolean = false) => {
    try {
      const result = await previewMutation.mutateAsync({
        filters,
        sample_size: sampleSize,
        offset,
        exclude_exported: excludeExported,
      });
      setPreviewData(result);
    } catch (error) {
      console.error('Preview failed:', error);
      alert(t('annotationExportPage.previewFailed') + ': ' + (error as Error).message);
    }
  };

  const handleExecute = async (
    filters: ExportFilters,
    sampleSize: number,
    title: string,
    description?: string,
    selectedIds?: string[],
    skipDuplicates?: boolean
  ) => {
    try {
      const result = await executeMutation.mutateAsync({
        filters,
        sample_size: sampleSize,
        batch_title: title,
        batch_description: description,
        selected_ids: selectedIds,
        skip_duplicates: skipDuplicates ?? true,
      });

      alert(t('annotationExportPage.exportSuccess') + `: ${result.batch_code} (${result.tasks_created})`);
      navigate('/annotation/batches');
    } catch (error) {
      console.error('Export failed:', error);
      alert(t('annotationExportPage.exportFailed') + ': ' + (error as Error).message);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('annotationBatchesPage.pageTitle')}</h1>

        {/* Sub-navigation Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {TAB_PATHS.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`
                  flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors
                  ${isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {t(`annotationBatchesPage.tabs.${tab.key}`)}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Page Description */}
      <div className="mb-6">
        <p className="text-gray-600">{t('annotationExportPage.pageDescription')}</p>
      </div>

      {/* Export Preview Component */}
      <ExportPreview
        onPreview={handlePreview}
        onExecute={handleExecute}
        previewData={previewData || undefined}
        isLoading={previewMutation.isPending}
        isExecuting={executeMutation.isPending}
      />
    </div>
  );
}
